import { useTranslation } from "react-i18next";

import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { Alert, Box, CircularProgress, InputAdornment, Stack, Typography } from "@mui/material";

import { FormDialog } from "../../../../form-dialog/FormDialog";
import { FormikDecimalTextField } from "../../../../formik/FormikDecimalTextField";
import { FormikAutocompleteSelect } from "../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "../../../../formik/formik-date-picker/FormikDatePicker";
import { PERCENTAGE_MAX_DECIMALS } from "./BalanceSurchargeDialog.utils";
import { BalanceSurchargePreview } from "./BalanceSurchargePreview";
import { BalanceSurchargeRows } from "./BalanceSurchargeRows";
import { useBalanceSurcharge } from "./hooks/useBalanceSurcharge";

interface Props {
  clientId: string;
  onClose: () => void;
}

const DIALOG_WIDTH = 520;

export function BalanceSurchargeDialog({ clientId, onClose }: Props) {
  const { t } = useTranslation();
  const {
    formik,
    entryTypeOptions,
    isSrvRegisteredEntryType,
    rows,
    processableCount,
    skippedCount,
    scope,
    newTotal,
    difference,
    hasPreview,
    isLoading,
  } = useBalanceSurcharge(clientId, onClose);

  return (
    <FormDialog
      title={t("claims.surcharge.dialogTitle")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("claims.surcharge.submit")}
      submitDisabled={processableCount === 0}
      submitStartIcon={<TrendingUpRoundedIcon />}
      // Nothing is drafted here that survives closing — a percentage and a date are retyped in
      // seconds — so Abbrechen closes straight away instead of asking to discard changes.
      confirmDirtyClose={false}
      fullWidth
      paperSx={{ maxWidth: { md: DIALOG_WIDTH, lg: DIALOG_WIDTH } }}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress aria-label={t("claims.surcharge.loading")} />
        </Box>
      ) : (
        <Stack gap={3}>
          <Typography variant="body2" color="text.secondary">
            {t("claims.surcharge.description")}
          </Typography>

          {skippedCount > 0 &&
            (processableCount === 0 ? (
              <Alert severity="warning">{t("claims.surcharge.nothingToApply", { count: rows.length })}</Alert>
            ) : (
              <Alert severity="info">
                {t("claims.surcharge.skippedHint", { count: skippedCount, selected: rows.length })}
              </Alert>
            ))}

          <Box display="flex" gap={2}>
            <Box flex={1}>
              <FormikDecimalTextField
                formik={formik}
                path="percentage"
                label={t("claims.surcharge.percentage")}
                required
                // minValue keeps a negative out of the field entirely; the schema catches empty
                // and zero.
                minValue={0}
                // A percentage is not an amount: 50 stays "50" rather than becoming "50,00",
                // and any number of decimals is accepted. Right-aligned so the number sits
                // against the %.
                decimalScale={PERCENTAGE_MAX_DECIMALS}
                fixedDecimalScale={false}
                textFieldProps={{ sx: { "& input": { textAlign: "right" } } }}
                slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
              />
            </Box>
            <Box flex={1}>
              <FormikDatePicker formik={formik} path="date" label={t("claims.surcharge.date")} required />
            </Box>
          </Box>

          {/* No `optional` marker: the two required fields carry the asterisk, so this one
              reading plain is what tells the user it may be left empty. The type marked as the
              SRV registration is offered but disabled — a claim may carry only one entry of it,
              so a bulk write would break every claim already registered — and the option itself
              says why. */}
          <FormikAutocompleteSelect
            formik={formik}
            path="entryType"
            label={t("claims.surcharge.entryType")}
            options={entryTypeOptions}
            getOptionDisabled={(option) => isSrvRegisteredEntryType(option.id)}
            renderOption={({ key, ...optionProps }, option) => (
              <li key={key} {...optionProps}>
                {isSrvRegisteredEntryType(option.id) ? (
                  <Stack>
                    <span>{option.label}</span>
                    <Typography variant="caption" color="text.secondary">
                      {t("claims.surcharge.registeredTypeUnavailable")}
                    </Typography>
                  </Stack>
                ) : (
                  option.label
                )}
              </li>
            )}
          />

          {/* Above the numbers, because it is what explains a difference of 0,00 €. A warning
              rather than a note once no claim is left for the surcharge to raise. */}
          {scope.unchangedCount > 0 && (
            <Alert severity={scope.unchangedCount === scope.affectedCount ? "warning" : "info"}>
              {t("claims.surcharge.pastDateHint", {
                count: scope.affectedCount,
                unchanged: scope.unchangedCount,
              })}
            </Alert>
          )}

          <BalanceSurchargeRows rows={rows} hasPreview={hasPreview} />

          <BalanceSurchargePreview scope={scope} newTotal={newTotal} difference={difference} hasPreview={hasPreview} />
        </Stack>
      )}
    </FormDialog>
  );
}
